/**
 * The menu on the guests' phones (docs/01-architektur.md, Karte im Netz): layout D as in KellerLog,
 * one column for a narrow screen. „Veröffentlichen“ freezes the menu as it is (`freezeMenu`); the
 * server turns the frozen state into a page here. The state comes from a device, so it is read field
 * by field (`publishedOf`) and every text is escaped: no device ever hands the server finished HTML.
 *
 * With a drinks card of KellerLog connected and published, the page has two tabs at the bottom,
 * „Speisen“ and „Getränke“, switched by CSS alone (hidden radio buttons). KellerLog's page stands in
 * a frame without rights (`<iframe sandbox srcdoc>`): it keeps its look and its jump marks and can
 * run nothing on this page.
 *
 * The business's logo (S13) is frozen with the menu and stands in the page itself (a data: address),
 * so the page still loads nothing from elsewhere.
 */
import { cleanWindow, inWindow, windowText } from "./hours.ts";
import type { Window } from "./hours.ts";
import { columnPrices, legendLines, showsPrices } from "./menu.ts";
import { fitLogo, logoOf, logoPlaceOf, showsHouse } from "./logo.ts";
import type { Logo, LogoPlace } from "./logo.ts";
import type { MenuView } from "./menu.ts";
import type { Allergen } from "./allergens.ts";
import { CHOICE_TEXT, COURSE_MARK_TEXT, DRINK_STYLE_KEYS, SET_DRINK_STYLE_KEYS, setDrinkDesign, OR_TEXT, PRESETS, choiceTextOf, cssFamily, cssVariant, designFiles, designOf, fontFaceOf, orTextOf, unitParts } from "./design.ts";
import { tagIcon } from "./tag-icons.ts";
import { glued } from "./semantic-break.ts";
import { legendWithLead } from "./lead.ts";
import { HOUSE_LINKS, houseEmail, houseLinkLabel, housePhone, telLink } from "./house-links.ts";
import type { CourseMark, Design, SetDrinkStyleKey, StyleKey, TextStyle } from "./design.ts";
import type { PublishedLead } from "./lead.ts";
import { cardWords, completeLangs, viewIn } from "./card-lang.ts";
import type { Phrases } from "./phrases.ts";
import { LANGS, LANG_NATIVE, LANG_SHORT, isLang, tagIn } from "./translation.ts";
import type { Lang, Translatable } from "./translation.ts";

export interface PublishedItem {
  /** A text (S14): `name` its title, `description` its text. */
  text?: true;
  number: number | null;
  name: string;
  description: string;
  tags: string[];
  codes: string[];
  price: number | null;
  note: string;
  /** What the price is for (S19); only when set, so menus without it keep their digest. */
  priceLabel?: string;
  /** Further prices (S19); only when there are any. */
  variants?: { label: string; price: number | null; codes: string[] }[];
  /** Prices in columns under these heads (S22), the heads above the first; only when so. */
  cols?: string[];
  colHead?: true;
  /** Amounts as a column before the prices (S51); only when so. */
  amounts?: true;
  /** In a set menu (S52): a drink, `paired` to the course before it; only when so. */
  drink?: true;
  paired?: true;
  /** In a set menu (S32): an alternative to the item before, a surcharge, the title of the course it begins; only when so. */
  or?: true;
  surcharge?: number;
  course?: string;
  /** In a section of the card (S44): the title of the subsection this item begins; only when so. */
  sub?: string;
  /** The dish in the catalogue (S39), for „ausverkauft“; menus published before have none. Not in the digest. */
  dish?: string;
  /** Sold out now (S39): set for the page alone (`markSoldOut`), never stored. */
  soldOut?: true;
}

export interface PublishedSection {
  title: string;
  note: string;
  kind: "group" | "set";
  price: number | null;
  items: PublishedItem[];
  /** A section of the drinks (S22); only when so, so menus without drinks keep their digest. */
  part?: "drinks";
  /** A set menu's price label and further prices (S22); only when there are any. */
  priceLabel?: string;
  variants?: { label: string; price: number | null; codes: string[] }[];
  /** A section of the food (S46): over the column of its prices („30 cm“); only when so. */
  priceHead?: string;
  /** A set menu's section in the menu (S39), for „ausverkauft“ as a whole; menus published before have none. Not in the digest. */
  id?: string;
  /** A set menu sold out now (S39): set for the page alone (`markSoldOut`), never stored. */
  soldOut?: true;
  /** A temporary section (S62): on the page only at these times; only when so, so menus without keep their digest. */
  web?: Window;
}

/** The published menu: what the app froze when „Veröffentlichen“ was pressed. */
export interface PublishedMenu {
  menu: string;
  publishedAt: number;
  house: string;
  title: string;
  subtitle: string;
  note: string;
  sections: PublishedSection[];
  legend: Allergen[];
  /** The logo as it was when published (S13), or null. */
  logo: Logo | null;
  /** „Logo statt Name“: the logo alone at the top (only with a logo). */
  logoOnly: boolean;
  /** Where the logo stands. */
  logoPlace: LogoPlace;
  /** The menu's style (S15) when it is meant for the net too; null: the page's own look. */
  design?: Design | null;
  /** Tags drawn as symbols (S21), by the tag; only when there are any, so other menus keep their digest. */
  tagIcons?: Record<string, string>;
  /** The word between the alternatives of a course (S32), as the style says; only when not „oder“. */
  orText?: string;
  /** The word after the title of a course with a choice (S32 § 12); only when not „zur Wahl“. */
  choiceText?: string;
  /** The recommendations above the menu (S37, L2); only when there are some. */
  lead?: PublishedLead;
  /** The texts in further languages (S36), frozen with the rest; only when the business has some. */
  texts?: PublishedTexts;
}

/**
 * The texts of a published menu in further languages (S36), by language: for each section and item
 * as frozen, its name, description and (where they differ) tags; the legend's names in its order.
 * The card's own texts from the dictionary (31.10.2026) only where they differ from the German, so a
 * menu without them keeps its digest: the head, each section's, an item's note, course, labels.
 */
export type PublishedTexts = Partial<Record<Lang, LangTexts>>;
export interface LangTexts {
  items: ({ name: string; description: string; tags?: string[] } & ItemWords)[][];
  legend: string[];
  head?: { title?: string; subtitle?: string; note?: string };
  sections?: ({ title?: string; note?: string; priceLabel?: string; priceHead?: string; labels?: string[] } | null)[];
  /** The tags with a symbol (S21) by their name in the language, where it differs. */
  icons?: Record<string, string>;
}
interface ItemWords {
  note?: string;
  course?: string;
  sub?: string;
  priceLabel?: string;
  labels?: string[];
  cols?: string[];
}

/** What the business puts at the top of every menu besides its name (S13). */
export interface Brand {
  logo: Logo | null;
  logoOnly: boolean;
  logoPlace?: LogoPlace;
  /** The menu's style, when the business has made one (S15). */
  design?: Design | null;
}

/** The business's lines for the foot of the card. */
export interface WebSettings {
  address: string;
  /** Link to the house's imprint; only https. */
  imprint: string;
  /** Link to the house's privacy policy; only https; empty: none (it may stand on the imprint's page). */
  privacy?: string;
  /** „Alle Preise in Euro inkl. MwSt.“ */
  footer: string;
  /** Phone and e-mail of the house (S50): links at the foot. */
  phone?: string;
  email?: string;
  /** The house's website, Instagram and Facebook (S50): links at the foot. */
  website?: string;
  instagram?: string;
  facebook?: string;
  /** Where the tab „Getränke“ comes from (S59); empty or missing: the menu's own drinks, else KellerLog's. */
  drinksFrom?: DrinksFrom;
}

/**
 * Where the tab „Getränke“ of the QR-Code-Speisekarte comes from (S59): `menu` the drinks of the menu
 * (or of the fixed drinks menu), `kellerlog` KellerLog's card, `none` no tab; "" the rule from before.
 */
export type DrinksFrom = "" | "menu" | "kellerlog" | "none";
export const DRINKS_FROM = ["", "menu", "kellerlog", "none"] as const;
/** Whether KellerLog's card may come into the tab (S59). */
export const kellerlogShown = (s: { drinksFrom?: DrinksFrom }) => !s.drinksFrom || s.drinksFrom === "kellerlog";

export const DEFAULT_FOOTER = "Alle Preise in Euro inkl. MwSt.";

export function freezeMenu(menuId: string, view: MenuView, house: string, at: number, brand: Brand = { logo: null, logoOnly: false }): PublishedMenu {
  return {
    menu: menuId,
    publishedAt: at,
    house,
    title: view.title,
    subtitle: view.subtitle,
    note: view.note,
    sections: view.sections
      .filter((s) => s.items.length > 0)
      .map((s) => ({
        title: s.title,
        note: s.note,
        kind: s.kind,
        price: s.price,
        items: s.items.map((i) => ({
          ...(i.text ? { text: true as const } : {}),
          number: i.number,
          name: i.name,
          description: i.description,
          tags: i.tags,
          codes: i.codes,
          price: i.price,
          note: i.note,
          ...(i.priceLabel ? { priceLabel: i.priceLabel } : {}),
          ...(i.variants.length ? { variants: i.variants.map((v) => ({ label: v.label, price: v.price, codes: v.codes })) } : {}),
          ...(i.cols ? { cols: [...i.cols] } : {}),
          ...(i.colHead ? { colHead: true as const } : {}),
          ...(i.amounts ? { amounts: true as const } : {}),
          ...(i.drink ? { drink: true as const } : {}),
          ...(i.paired ? { paired: true as const } : {}),
          ...(i.or ? { or: true as const } : {}),
          ...(i.surcharge ? { surcharge: i.surcharge } : {}),
          ...(i.course ? { course: i.course } : {}),
          ...(i.sub ? { sub: i.sub } : {}),
          ...(!i.text && i.dish ? { dish: i.dish } : {}),
        })),
        ...(s.part === "drinks" ? { part: "drinks" as const } : {}),
        ...(s.priceLabel ? { priceLabel: s.priceLabel } : {}),
        ...(s.variants?.length ? { variants: s.variants.map((v) => ({ label: v.label, price: v.price, codes: v.codes })) } : {}),
        ...(s.priceHead ? { priceHead: s.priceHead } : {}),
        ...(s.kind === "set" ? { id: s.id } : {}),
        // Last, as `sectionOf` reads it back: the digest of the frozen menu and of the one read again are the same.
        ...(s.web ? { web: { days: s.web.days, from: s.web.from, to: s.web.to } } : {}),
      })),
    legend: view.legend,
    logo: brand.logo,
    logoOnly: brand.logo !== null && brand.logoOnly,
    logoPlace: brand.logo ? logoPlaceOf(brand.logoPlace) : "center",
    design: brand.design?.web ? brand.design : null,
    ...(Object.keys(view.tagIcons).length ? { tagIcons: view.tagIcons } : {}),
    // The same word as on paper (S32), also when the style is not meant for the net.
    ...(brand.design && orTextOf(brand.design.orText) !== OR_TEXT.fallback ? { orText: orTextOf(brand.design.orText) } : {}),
    ...(brand.design && choiceTextOf(brand.design.choiceText) !== CHOICE_TEXT.fallback ? { choiceText: choiceTextOf(brand.design.choiceText) } : {}),
  };
}

/** The texts of a frozen menu (S36), as `PublishedTexts` keeps them for one language. */
function textsOfFrozen(p: PublishedMenu, german: PublishedMenu): LangTexts {
  const same = (a: readonly string[] | undefined, b: readonly string[] | undefined) => (a ?? []).join("\u0000") === (b ?? []).join("\u0000");
  // A field only where it differs from the German.
  const differ = <T extends object>(o: T): T | null => {
    const kept = Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
    return Object.keys(kept).length ? kept : null;
  };
  const one = (t: string | undefined, g: string | undefined) => (t !== undefined && t !== g ? t : undefined);
  const labels = (x: { variants?: { label: string }[] }, g: { variants?: { label: string }[] } | undefined) => {
    const a = (x.variants ?? []).map((v) => v.label);
    return same(a, (g?.variants ?? []).map((v) => v.label)) ? undefined : a;
  };
  const head = differ({ title: one(p.title, german.title), subtitle: one(p.subtitle, german.subtitle), note: one(p.note, german.note) });
  const sections = p.sections.map((s, k) => {
    const g = german.sections[k];
    return differ({ title: one(s.title, g?.title), note: one(s.note, g?.note), priceLabel: one(s.priceLabel, g?.priceLabel), priceHead: one(s.priceHead, g?.priceHead), labels: labels(s, g) });
  });
  const icons = p.tagIcons && !same(Object.keys(p.tagIcons), Object.keys(german.tagIcons ?? {})) ? p.tagIcons : undefined;
  return {
    items: p.sections.map((s, k) =>
      s.items.map((i, j) => {
        const g = german.sections[k]?.items[j];
        const words = differ<ItemWords>({ note: one(i.note, g?.note), course: one(i.course, g?.course), sub: one(i.sub, g?.sub), priceLabel: one(i.priceLabel, g?.priceLabel), labels: labels(i, g), cols: same(i.cols, g?.cols) ? undefined : i.cols });
        return { name: i.name, description: i.description, ...(same(i.tags, g?.tags) ? {} : { tags: i.tags }), ...(words ?? {}) };
      }),
    ),
    legend: p.legend.map((a) => a.name),
    ...(head ? { head } : {}),
    ...(sections.some(Boolean) ? { sections } : {}),
    ...(icons ? { icons } : {}),
  };
}

/**
 * `freezeMenu` with the texts in the business's further languages (S36): the server when publishing,
 * the app to see whether the net shows the newest. Without languages exactly `freezeMenu`.
 */
export function freezeWithTexts(menuId: string, view: MenuView, house: string, at: number, brand: Brand | undefined, dishes: ReadonlyMap<string, Translatable>, langs: readonly Lang[], phrases: Phrases = {}): PublishedMenu {
  const frozen = freezeMenu(menuId, view, house, at, brand);
  if (!langs.length) return frozen;
  const texts: PublishedTexts = {};
  for (const lang of langs) texts[lang] = textsOfFrozen(freezeMenu(menuId, viewIn(view, dishes, { lang, both: false }, phrases), house, at, brand), frozen);
  return { ...frozen, texts };
}

/**
 * `freezeWithTexts` for the net (Martin, 01.11.2026): only the languages the card is wholly in
 * (`completeLangs`), so a guest never gets half a page in German; one that becomes complete changes
 * what is frozen, and the app says „neu veröffentlichen“.
 */
export function freezeForNet(menuId: string, view: MenuView, house: string, at: number, brand: Brand | undefined, dishes: ReadonlyMap<string, Translatable>, langs: readonly Lang[], phrases: Phrases = {}): PublishedMenu {
  return freezeWithTexts(menuId, view, house, at, brand, dishes, completeLangs(view, dishes, langs, phrases), phrases);
}

/** A published menu in a further language (S36): its texts where frozen, else German as it is. */
export function menuIn<M extends Pick<PublishedMenu, "title" | "subtitle" | "note" | "sections" | "legend" | "tagIcons"> & { texts?: PublishedTexts; lead?: PublishedLead }>(m: M, lang: Lang): M {
  const apply = <P extends Pick<PublishedMenu, "title" | "subtitle" | "note" | "sections" | "legend" | "tagIcons"> & { texts?: PublishedTexts }>(p: P): P => {
    const t = p.texts?.[lang];
    if (!t) return p;
    // Labels in their order where the translation has as many.
    const relabel = <V extends { label: string }>(v: V[] | undefined, labels: string[] | undefined) => (v && labels && labels.length === v.length ? v.map((x, k) => ({ ...x, label: labels[k]! })) : v);
    const item = (i: PublishedItem, x: LangTexts["items"][number][number] | undefined): PublishedItem => {
      if (!x) return i;
      const variants = relabel(i.variants, x.labels);
      return { ...i, name: x.name, description: x.description, ...(x.tags ? { tags: x.tags } : {}), ...(x.note !== undefined ? { note: x.note } : {}), ...(x.course !== undefined && i.course ? { course: x.course } : {}), ...(x.sub !== undefined && i.sub ? { sub: x.sub } : {}), ...(x.priceLabel !== undefined && i.priceLabel ? { priceLabel: x.priceLabel } : {}), ...(variants ? { variants } : {}), ...(x.cols && i.cols && x.cols.length === i.cols.length ? { cols: x.cols } : {}) };
    };
    return {
      ...p,
      ...(t.head?.title !== undefined ? { title: t.head.title } : {}),
      ...(t.head?.subtitle !== undefined ? { subtitle: t.head.subtitle } : {}),
      ...(t.head?.note !== undefined ? { note: t.head.note } : {}),
      sections: p.sections.map((s, k) => {
        const w = t.sections?.[k];
        const variants = relabel(s.variants, w?.labels ?? undefined);
        return { ...s, ...(w?.title !== undefined ? { title: w.title } : {}), ...(w?.note !== undefined ? { note: w.note } : {}), ...(w?.priceLabel !== undefined && s.priceLabel ? { priceLabel: w.priceLabel } : {}), ...(w?.priceHead !== undefined && s.priceHead ? { priceHead: w.priceHead } : {}), ...(variants ? { variants } : {}), items: s.items.map((i, j) => item(i, t.items[k]?.[j])) };
      }),
      legend: p.legend.map((a, k) => ({ ...a, name: t.legend[k] || a.name })),
      ...(p.tagIcons ? { tagIcons: t.icons ?? Object.fromEntries(Object.entries(p.tagIcons).map(([tag, ic]) => [tagIn(tag, lang), ic])) } : {}),
    };
  };
  return { ...apply(m), ...(m.lead ? { lead: apply(m.lead) } : {}) };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
/** Text with its units (S46) in a span of their own, which „GROSS“ and small capitals leave as they are. */
/** A text the browser breaks into lines (S58): short leading words joined to the next. */
const escText = (s: string) => esc(glued(s));
const escUnits = (s: string) => unitParts(s).map((p) => (p.unit ? `<span class="un">${esc(p.text)}</span>` : esc(p.text))).join("");
const text = (v: unknown, max = 2000): string => (typeof v === "string" ? v.slice(0, max) : "");
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null);
const list = (v: unknown, max: number): unknown[] => (Array.isArray(v) ? v.slice(0, max) : []);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const words = (v: unknown, max = 40, len = 80) => list(v, max).map((x) => text(x, len)).filter((x) => x !== "");

function itemOf(v: unknown): PublishedItem | null {
  const i = obj(v);
  const name = text(i.name, 200);
  const surcharge = int(i.surcharge);
  const courseTitle = text(i.course, 60);
  const subTitle = text(i.sub, 60);
  const course = { ...(i.or === true ? { or: true as const } : {}), ...(i.paired === true ? { paired: true as const } : {}), ...(surcharge && surcharge > 0 ? { surcharge } : {}), ...(courseTitle ? { course: courseTitle } : {}), ...(subTitle ? { sub: subTitle } : {}) };
  if (i.text === true) {
    const body = text(i.description, 800);
    return name || body ? { text: true, number: null, name, description: body, tags: [], codes: [], price: null, note: "", ...course } : null;
  }
  if (!name) return null;
  const priceLabel = text(i.priceLabel, 40);
  const variants = linesOf(i.variants, 10);
  const cols = words(i.cols, 4, 40);
  return {
    number: int(i.number),
    name,
    description: text(i.description, 800),
    tags: words(i.tags, 20),
    codes: words(i.codes, 40, 8),
    price: int(i.price),
    note: text(i.note, 200),
    ...(priceLabel ? { priceLabel } : {}),
    ...(variants.length ? { variants } : {}),
    ...(cols.length ? { cols } : {}),
    ...(cols.length && i.colHead === true ? { colHead: true as const } : {}),
    ...(!cols.length && i.amounts === true && priceLabel ? { amounts: true as const } : {}),
    ...(i.drink === true ? { drink: true as const } : {}),
    ...course,
    ...(typeof i.dish === "string" && DISH_ID.test(i.dish) ? { dish: i.dish } : {}),
  };
}

const DISH_ID = /^[A-Za-z0-9-]{1,64}$/;

const linesOf = (v: unknown, max: number) => list(v, max).map(obj).map((x) => ({ label: text(x.label, 40), price: int(x.price), codes: words(x.codes, 40, 8) })).filter((x) => x.label !== "");

function sectionOf(v: unknown): PublishedSection {
  const s = obj(v);
  const set = s.kind === "set";
  const priceLabel = set ? text(s.priceLabel, 40) : "";
  const variants = set ? linesOf(s.variants, 6) : [];
  const priceHead = !set && s.part !== "drinks" ? text(s.priceHead, 40) : "";
  return {
    title: text(s.title, 120),
    note: text(s.note, 600),
    kind: set ? "set" : "group",
    price: int(s.price),
    items: list(s.items, 500).map(itemOf).filter((i): i is PublishedItem => i !== null),
    ...(s.part === "drinks" ? { part: "drinks" as const } : {}),
    ...(priceLabel ? { priceLabel } : {}),
    ...(variants.length ? { variants } : {}),
    ...(priceHead ? { priceHead } : {}),
    ...(set && typeof s.id === "string" && DISH_ID.test(s.id) ? { id: s.id } : {}),
    ...(cleanWindow(s.web) ? { web: cleanWindow(s.web)! } : {}),
  };
}

/**
 * The page at a moment (S62, Stufe 1): a temporary section only in its time; one that is not shown
 * leaves a line at the top in the guest's language („Mittagstisch Mo–Fr 11:30–14:30“, Martin F3: left
 * out, not pale); one that is shown has its time over its note. The recommendations alike.
 */
export function sectionsAt<M extends { sections: PublishedSection[]; lead?: PublishedLead }>(m: M, at: number, lang: Lang | "de" = "de"): { menu: M; hints: string[] } {
  const hints: string[] = [];
  const at_ = (list: PublishedSection[]) =>
    list.flatMap((s) => {
      if (!s.web) return [s];
      const when = windowText(s.web, lang);
      if (!inWindow(s.web, at)) {
        hints.push(s.title ? `${s.title} ${when}` : when);
        return [];
      }
      return [{ ...s, note: s.note ? `${when} · ${s.note}` : when }];
    });
  const menu = { ...m, sections: at_(m.sections), ...(m.lead ? { lead: { ...m.lead, sections: at_(m.lead.sections) } } : {}) };
  return { menu, hints };
}

/** Which temporary sections a page shows at a moment, as a key for the page's store (S62). */
export const shownKey = (m: { sections: PublishedSection[]; lead?: PublishedLead } | null, at: number) => (m ? [...m.sections, ...(m.lead?.sections ?? [])].map((s) => (s.web ? (inWindow(s.web, at) ? "1" : "0") : "")).join("") : "");

/** The published menu from what is stored, or null: nothing published. */
export function publishedOf(v: unknown): PublishedMenu | null {
  const p = obj(v);
  if (int(p.publishedAt) === null || !Array.isArray(p.sections)) return null;
  const logo = logoOf(p.logo);
  return {
    menu: text(p.menu, 64),
    publishedAt: int(p.publishedAt)!,
    house: text(p.house, 120),
    title: text(p.title, 120),
    subtitle: text(p.subtitle, 160),
    note: text(p.note, 600),
    sections: list(p.sections, 50).map(sectionOf),
    legend: legendOf(p.legend),
    logo,
    logoOnly: logo !== null && p.logoOnly === true,
    logoPlace: logo ? logoPlaceOf(p.logoPlace) : "center",
    design: p.design ? designOf(p.design) : null,
    ...iconsOf(p.tagIcons),
    ...(typeof p.orText === "string" && orTextOf(p.orText) !== OR_TEXT.fallback ? { orText: orTextOf(p.orText) } : {}),
    ...(typeof p.choiceText === "string" && choiceTextOf(p.choiceText) !== CHOICE_TEXT.fallback ? { choiceText: choiceTextOf(p.choiceText) } : {}),
    ...leadField(p.lead),
    ...textsField(p.texts),
  };
}

/** The texts in further languages as stored (S36), field by field; only known languages. */
function textsField(v: unknown): { texts?: PublishedTexts } {
  const out: PublishedTexts = {};
  for (const [lang, t] of Object.entries(obj(v))) {
    if (!isLang(lang)) continue;
    const o = obj(t);
    // The card's own words (31.10.2026): each only where it is there.
    const opt = (x: Record<string, unknown>, key: string, max: number) => (typeof x[key] === "string" ? { [key]: text(x[key], max) } : {});
    const labels = (x: Record<string, unknown>, key: string) => (Array.isArray(x[key]) ? { [key]: words(x[key], 10, 40) } : {});
    const head = o.head && typeof o.head === "object" ? { ...opt(obj(o.head), "title", 120), ...opt(obj(o.head), "subtitle", 160), ...opt(obj(o.head), "note", 600) } : null;
    const sections = Array.isArray(o.sections) ? list(o.sections, 50).map((v) => (v && typeof v === "object" ? { ...opt(obj(v), "title", 120), ...opt(obj(v), "note", 600), ...opt(obj(v), "priceLabel", 40), ...opt(obj(v), "priceHead", 40), ...labels(obj(v), "labels") } : null)) : null;
    const icons = o.icons && typeof o.icons === "object" ? Object.fromEntries(Object.entries(obj(o.icons)).slice(0, 40).filter((e): e is [string, string] => typeof e[1] === "string").map(([k, v]) => [k.slice(0, 80), v.slice(0, 40)])) : null;
    out[lang] = {
      items: list(o.items, 50).map((s) => list(s, 400).map((i) => { const x = obj(i); return { name: text(x.name, 200), description: text(x.description, 2000), ...(Array.isArray(x.tags) ? { tags: words(x.tags) } : {}), ...opt(x, "note", 200), ...opt(x, "course", 60), ...opt(x, "sub", 60), ...opt(x, "priceLabel", 40), ...labels(x, "labels"), ...labels(x, "cols") }; })),
      legend: list(o.legend, 60).map((n) => text(n, 80)),
      ...(head && Object.keys(head).length ? { head } : {}),
      ...(sections ? { sections } : {}),
      ...(icons ? { icons } : {}),
    };
  }
  return Object.keys(out).length ? { texts: out } : {};
}

const legendOf = (v: unknown) =>
  list(v, 60)
    .map((a) => {
      const o = obj(a);
      return { code: text(o.code, 8), name: text(o.name, 80), kind: o.kind === "additive" ? ("additive" as const) : ("allergen" as const) };
    })
    .filter((a) => a.code !== "");

/** The recommendations as stored (S37, L2), read field by field like the rest. */
function leadField(v: unknown): { lead?: PublishedLead } {
  const l = obj(v);
  if (!Array.isArray(l.sections)) return {};
  return { lead: { menu: text(l.menu, 64), title: text(l.title, 120), subtitle: text(l.subtitle, 160), note: text(l.note, 600), sections: list(l.sections, 50).map(sectionOf), legend: legendOf(l.legend), ...iconsOf(l.tagIcons), ...textsField(l.texts) } };
}

/** The symbols of the tags as stored: only known symbols, only when there are any. */
function iconsOf(v: unknown): { tagIcons?: Record<string, string> } {
  const icons = Object.entries(obj(v)).filter((e): e is [string, string] => typeof e[1] === "string" && tagIcon(e[1]) !== undefined).slice(0, 40);
  return icons.length ? { tagIcons: Object.fromEntries(icons.map(([t, i]) => [t.slice(0, 80), i])) } : {};
}

/**
 * A short mark of what a published menu shows: the app compares the menu as it is now with the one on
 * the net („Im Netz steht noch der Stand vom …“) without holding the whole published state. The logo
 * counts by when it was uploaded, so the app need not hold its picture for this.
 */
/** What of a style the net shows: the logo's size (S13), the logo in the foot (S43) and the setting in the middle (S48) are only for paper. */
const webPart = ({ logoSize: _size, footLogo: _foot, footLogoSize: _footSize, align: _align, ...d }: Design) => {
  // A style that came later and only says what a style from before would get (designOf) is left out,
  // so a card published before it keeps its digest: „Unterabschnitt“ (S44) as the course, the drinks'
  // „Mengen über den Preisen“ (S46) as the food's.
  const same = (a: TextStyle | undefined, b: TextStyle) => JSON.stringify(a) === JSON.stringify(b);
  const { subsection, ...styles } = d.styles;
  const drinks = d.drinkStyles && same(d.drinkStyles.sizeHead, d.styles.sizeHead) ? (({ sizeHead: _head, ...rest }) => rest)(d.drinkStyles) : d.drinkStyles;
  return { ...d, styles: same(subsection, d.styles.course) ? styles : d.styles, ...(drinks ? { drinkStyles: drinks } : {}) };
};

export function digestOf(p: Pick<PublishedMenu, "house" | "title" | "subtitle" | "note" | "sections" | "legend" | "tagIcons" | "orText" | "choiceText" | "lead" | "texts"> & { logo: { at: number } | null; logoOnly: boolean; logoPlace?: LogoPlace; design?: Design | null }): string {
  const place = logoPlaceOf(p.logoPlace);
  const brand = [...(p.logo ? [p.logo.at, p.logoOnly, ...(place === "center" ? [] : [place])] : []), ...(p.design?.web ? [JSON.stringify(webPart(designOf(p.design)))] : [])];
  const icons = p.tagIcons && Object.keys(p.tagIcons).length ? [p.tagIcons] : [];
  const or = [...(p.orText && p.orText !== OR_TEXT.fallback ? [p.orText] : []), ...(p.choiceText && p.choiceText !== CHOICE_TEXT.fallback ? [{ choice: p.choiceText }] : [])];
  // Which dish an item is and which section a set menu (S39) is no change of what the guests see.
  const plainOf = (list: PublishedSection[]) => list.map(({ id: _id, soldOut: _sold, ...s }) => ({ ...s, items: s.items.map(({ dish: _dish, soldOut: _soldOut, ...i }) => i) }));
  const sections = plainOf(p.sections);
  // The recommendations (S37) at the end, so menus without keep their digest.
  const lead = p.lead ? [{ lead: [p.lead.menu, p.lead.title, p.lead.subtitle, p.lead.note, plainOf(p.lead.sections), p.lead.legend.map((a) => [a.code, a.name, a.kind]), p.lead.tagIcons ?? {}, ...(p.lead.texts ? [{ texts: p.lead.texts }] : [])] }] : [];
  // The further languages (S36) at the very end, so menus without keep their digest.
  const texts = p.texts ? [{ texts: p.texts }] : [];
  const json = JSON.stringify([p.house, p.title, p.subtitle, p.note, sections, p.legend.map((a) => [a.code, a.name, a.kind]), ...brand, ...icons, ...or, ...lead, ...texts]);
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** The mark of the recommendations alone (S37, L4): whether they changed since they went on the net, whatever the menu did. */
export const leadDigestOf = (lead: PublishedLead) => digestOf({ house: "", title: "", subtitle: "", note: "", sections: [], legend: [], logo: null, logoOnly: false, lead });

/** Only an address that leaves for a web page: no script, no other scheme. */
export const imprintLink = (v: unknown): string => {
  const s = typeof v === "string" ? v.trim() : "";
  if (!/^https:\/\/[^\s"'<>]+$/i.test(s) || s.length > 300) return "";
  try {
    return new URL(s).protocol === "https:" ? s : "";
  } catch {
    return "";
  }
};

/** The house's links at the foot (S50): phone and e-mail to tap, its website, Instagram and Facebook; only https ones. */
function linksFoot(settings: WebSettings): string {
  const phone = housePhone(settings.phone);
  const email = houseEmail(settings.email);
  const contact = [
    ...(phone ? [`<a href="${esc(telLink(phone))}">${esc(phone)}</a>`] : []),
    ...(email ? [`<a href="mailto:${esc(email)}">${esc(email)}</a>`] : []),
  ];
  const links = [...contact, ...HOUSE_LINKS.flatMap((k) => {
    const href = imprintLink(settings[k]);
    return href ? [`<a href="${esc(href)}" rel="noopener noreferrer">${esc(k === "website" ? houseLinkLabel(k, href) : k === "instagram" ? "Instagram" : "Facebook")}</a>`] : [];
  })];
  return links.length ? `<p class="links">${links.join(" · ")}</p>` : "";
}

/** Prices with the euro sign and always with cents, so they stand one below the other. */
const euro = (cents: number | null) => (cents === null ? "" : `${(cents / 100).toFixed(2).replace(".", ",")} €`);

/** A text (S14): a title in the accent with its text below; a title or a text alone as a line of its own. */
function textItem(i: PublishedItem): string {
  if (!i.name || !i.description) return `<li class="x"><p class="tl">${escText(i.name || i.description)}</p></li>`;
  return `<li class="x"><b class="tt">${escText(i.name)}</b><p class="tb">${escText(i.description)}</p></li>`;
}

/**
 * The lines of a dish's prices (S19) below its name: the main price when it has a label, then the
 * further ones, each with its codes. In a set menu the labels alone.
 */
export function priceLines(i: Pick<PublishedItem, "price" | "priceLabel" | "variants">, set: boolean): { label: string; price: number | null; codes: string[]; main: boolean }[] {
  return [...(i.priceLabel && !set ? [{ label: i.priceLabel, price: i.price, codes: [], main: true }] : []), ...(i.variants ?? []).map((v) => ({ ...v, price: set ? null : v.price, main: false }))];
}

/** A symbol (S21) as SVG in the colour of its text; `label` for those who cannot see it. */
export function iconSvg(id: string, label: string): string {
  const icon = tagIcon(id);
  if (!icon) return esc(label);
  return `<svg class="ic" viewBox="0 0 24 24" role="img" aria-label="${esc(label)}"><title>${esc(label)}</title>${icon.d.map((d) => `<path d="${esc(d)}"/>`).join("")}</svg>`;
}

/** A price in a column (S22): without the euro sign, so the columns stay narrow; the heads say the amounts. */
const plain = (cents: number | null) => (cents === null ? "" : (cents / 100).toFixed(2).replace(".", ","));

/**
 * The items of a set menu (S32) with their courses, indented as on paper (§ 12): a course's title
 * one step in, „· zur Wahl“ after it when it has a choice; its dishes and „oder“ between them one
 * step further; a course without a title right after another close to it (both come).
 */
function setItems(items: PublishedItem[], icons: Record<string, string> = {}, orText = OR_TEXT.fallback, choiceText = CHOICE_TEXT.fallback, sold = "ausverkauft", mark: CourseMark | null = null): string {
  // Without any course's title each course apart (a tasting menu, one dish a course).
  const titled = items.some((i) => i.course);
  // „Zwischen den Gängen“ (S57): before each course but the first; not before „oder“, a drink to its
  // course (S52) or a course without a title that belongs to the one before.
  const markHtml = mark === "line" ? '<li class="cm l" aria-hidden="true"></li>' : mark ? `<li class="cm" aria-hidden="true">${COURSE_MARK_TEXT[mark]}</li>` : "";
  return items
    .map((it, k) => {
      const head = !it.or || k === 0;
      const choice = Boolean(items[k + 1]?.or);
      const title = head && it.course ? `<li class="gc">${escText(it.course)}${choice ? `<span class="zw"> · ${esc(choiceText)}</span>` : ""}</li>` : "";
      const or = !head ? `<li class="or">${esc(orText)}</li>` : "";
      const next = items[k + 1];
      // Joined: no line between two dishes of one course without a choice.
      const joined = titled && Boolean(next && !next.or && !next.course);
      const html = item(it, true, icons, Boolean(next?.or) || joined, sold);
      const starts = markHtml && k > 0 && !it.or && !it.paired && (!titled || Boolean(it.course));
      return `${starts ? markHtml : ""}${title}${or}${titled && head && !it.course && k > 0 ? html.replace('<li class="e', '<li class="e jn') : html}`;
    })
    .join("");
}

/** „ausverkauft“ after the name (S39); the rest of the item pale, its prices struck through. */
const soldOutBadge = (i: PublishedItem, sold: string) => (i.soldOut ? `<span class="sx">${esc(sold)}</span>` : "");

function item(i: PublishedItem, set: boolean, icons: Record<string, string> = {}, alt = false, sold = "ausverkauft"): string {
  if (i.text) return textItem(i);
  // A drink in a set menu (S52): below its course, its prices in a line, each label with its price.
  if (set && i.drink) {
    const pairs = [...(i.priceLabel || i.price === null ? [] : [{ label: "", price: i.price as number | null }]), ...priceLines(i, false)].filter((v) => v.price !== null);
    const prices = pairs.map((v) => `${v.label ? `<span class="vl">${escUnits(v.label)}</span>\u00a0` : ""}<span class="pr">${euro(v.price)}</span>`).join(" · ");
    const codes = i.codes.length ? `<sup>${esc(i.codes.join(","))}</sup>` : "";
    return `<li class="e wn${alt ? " alt" : ""}${i.soldOut ? " so" : ""}"><span class="no"></span><div class="nm"><b>${escText(i.name)}</b>${soldOutBadge(i, sold)}${codes}${i.description ? `<p class="d">${escText(i.description)}</p>` : ""}${prices ? `<p class="wp">${prices}</p>` : ""}${i.note ? `<p class="n">${escText(i.note)}</p>` : ""}</div><span class="pr"></span></li>`;
  }
  if (i.cols && !set) {
    const n = i.cols.length;
    const head = i.colHead ? `<li class="ch c${n}"><span></span><span></span>${i.cols.map((c) => `<span>${escUnits(c)}</span>`).join("")}</li>` : "";
    const codes = i.codes.length ? `<sup>${esc(i.codes.join(","))}</sup>` : "";
    const tags = i.tags.length ? `<span class="tags">${i.tags.map((t) => (icons[t] ? `<span class="sy">${iconSvg(icons[t]!, t)}</span>` : `<span>${esc(t)}</span>`)).join("")}</span>` : "";
    const prices = columnPrices(i).map((p) => `<span class="pr">${plain(p)}</span>`).join("");
    return `${head}<li class="e c${n}${i.soldOut ? " so" : ""}"><span class="no">${i.number === null ? "" : esc(String(i.number))}</span><div class="nm"><b>${escText(i.name)}</b>${soldOutBadge(i, sold)}${codes}${tags}${i.description ? `<p class="d">${escText(i.description)}</p>` : ""}${i.note ? `<p class="n">${escText(i.note)}</p>` : ""}</div>${prices}</li>`;
  }
  // Amounts as a column (S51): the first with its price on the name's row, the description beside the second.
  if (i.amounts && !set) {
    const codes = i.codes.length ? `<sup>${esc(i.codes.join(","))}</sup>` : "";
    const tags = i.tags.length ? `<span class="tags">${i.tags.map((t) => (icons[t] ? `<span class="sy">${iconSvg(icons[t]!, t)}</span>` : `<span>${esc(t)}</span>`)).join("")}</span>` : "";
    const rows = priceLines(i, false).map((v) => `<span class="ml">${escUnits(v.label)}</span><span class="pr">${euro(v.price)}</span>`);
    const left = [i.description ? `<p class="d">${escText(i.description)}</p>` : "", i.note ? `<p class="n">${escText(i.note)}</p>` : ""].filter(Boolean);
    const below = Array.from({ length: Math.max(rows.length - 1, left.length) }, (_, k) => `<span></span><div class="nm">${left[k] ?? ""}</div>${rows[k + 1] ?? "<span></span><span></span>"}`).join("");
    return `<li class="e am${i.soldOut ? " so" : ""}"><span class="no">${i.number === null ? "" : esc(String(i.number))}</span><div class="nm"><b>${escText(i.name)}</b>${soldOutBadge(i, sold)}${codes}${tags}</div>${rows[0]}${below}</li>`;
  }
  const codes = i.codes.length ? `<sup>${esc(i.codes.join(","))}</sup>` : "";
  const tags = i.tags.length ? `<span class="tags">${i.tags.map((t) => (icons[t] ? `<span class="sy">${iconSvg(icons[t]!, t)}</span>` : `<span>${esc(t)}</span>`)).join("")}</span>` : "";
  const desc = i.description ? `<p class="d">${escText(i.description)}</p>` : "";
  const note = i.note ? `<p class="n">${escText(i.note)}</p>` : "";
  // Further prices as rows of the same grid: no number, the label, the price at the right.
  const lines = priceLines(i, set)
    .map((v) => `<span></span><span class="vl">${escUnits(v.label)}${v.codes.length ? `<sup>${esc(v.codes.join(","))}</sup>` : ""}</span><span class="pr">${euro(v.price)}</span>`)
    .join("");
  // In a set menu only a surcharge (S32): „+ 8,00 €“.
  const main = set ? (i.surcharge ? `+ ${euro(i.surcharge)}` : "") : i.priceLabel ? "" : euro(i.price);
  return `<li class="e${alt ? " alt" : ""}${i.or ? " ao" : ""}${i.soldOut ? " so" : ""}"><span class="no">${i.number === null ? "" : esc(String(i.number))}</span><div class="nm"><b>${escText(i.name)}</b>${soldOutBadge(i, sold)}${codes}${tags}${desc}${note}</div><span class="pr">${main}</span>${lines}</li>`;
}

const CSS = `
:root { --bg: #fbf8f4; --ink: #2a2522; --muted: #756a62; --line: #e4dad1; --accent: #a2461a; --nav: rgba(251,248,244,0.96); --tabbar: 3.4rem; --page: 40rem; }
@media (prefers-color-scheme: dark) { :root { --bg: #1d1a18; --ink: #ece4dc; --muted: #a59a91; --line: #3a3430; --accent: #f0a070; --nav: rgba(29,26,24,0.96); } }
* { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; scroll-padding-top: 3.6rem; }
body { margin: 0; background: var(--bg); color: var(--ink); font: 17px/1.45 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif; }
main { max-width: var(--page); margin: 0 auto; padding: 0 16px 2rem; }
header { max-width: var(--page); margin: 0 auto; text-align: center; padding: 1.6rem 16px 0.8rem; }
header .logo { display: block; margin: 0 auto 0.6rem; max-width: 100%; height: auto; }
@media (prefers-color-scheme: dark) { header .logo { background: #fbf8f4; padding: 0.5rem; border-radius: 10px; } }
header.side { display: flex; align-items: center; gap: 1rem; text-align: left; }
header.side.right { flex-direction: row-reverse; }
header.side .logo { margin: 0; flex: none; max-width: 40%; }
header.side .head { flex: 1; min-width: 0; }
header.side.left .head { margin-left: auto; }
header .house { font-size: 1.9rem; letter-spacing: 0.06em; font-variant: small-caps; line-height: 1.15; overflow-wrap: anywhere; }
header .sub { font: 0.75rem system-ui, sans-serif; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-top: 0.3rem; }
header .lead { font-size: 0.95rem; font-style: italic; color: var(--muted); margin: 0.5rem 0 0; }
nav.langs { max-width: var(--page); margin: 0 auto; padding: 0.5rem 16px 0; display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 0 0.1rem; font: 0.85rem system-ui, sans-serif; color: var(--muted); }
nav.langs a { color: var(--accent); text-decoration: none; padding: 0.4rem 0.45rem; }
nav.langs b { color: var(--ink); padding: 0.4rem 0.45rem; }
nav.jump { position: sticky; top: 0; z-index: 1; background: var(--nav); border-bottom: 1px solid var(--line); display: flex; gap: 0.4rem; overflow-x: auto; padding: 0.55rem max(16px, calc((100% - var(--page)) / 2 + 16px)); scrollbar-width: none; }
nav.jump::-webkit-scrollbar { display: none; }
nav.jump a { flex: none; font: 0.9rem system-ui, sans-serif; color: var(--ink); text-decoration: none; border: 1px solid var(--line); border-radius: 999px; padding: 0.3rem 0.8rem; white-space: nowrap; }
h2 { font-size: 1.5rem; font-weight: normal; font-variant: small-caps; letter-spacing: 0.05em; color: var(--accent); margin: 1.8rem 0 0.2rem; display: flex; justify-content: space-between; align-items: baseline; gap: 0.6rem; }
h2 .pr { font-variant: normal; font-size: 1.1rem; color: var(--ink); white-space: nowrap; }
.note { font-size: 0.88rem; font-style: italic; color: var(--muted); margin: 0 0 0.4rem; hyphens: auto; -webkit-hyphens: auto; text-wrap: pretty; }
.tnote { font-size: 0.88rem; color: var(--muted); text-align: center; margin: 0.4rem 0 1rem; padding: 0.45rem 0.7rem; border-radius: 8px; background: rgba(140, 110, 70, 0.1); text-wrap: pretty; }
ul.items { list-style: none; margin: 0; padding: 0; }
.e { display: grid; grid-template-columns: var(--no) minmax(0, 1fr) auto; column-gap: 0.5rem; align-items: baseline; padding: 0.55rem 0; border-bottom: 1px dotted var(--line); }
.e:last-child, .x:last-child { border-bottom: 0; }
.x { padding: 0.7rem 0; border-bottom: 1px dotted var(--line); }
.x .tt { display: block; font-weight: normal; font-style: italic; font-size: 1.15rem; color: var(--accent); }
.x .tl { margin: 0; font-weight: 600; font-size: 1.05rem; }
.x .tb { margin: 0.15rem 0 0; hyphens: auto; -webkit-hyphens: auto; text-wrap: pretty; }
body { --no: 2.2rem; }
.unnumbered { --no: 0; }
.unnumbered .e, .unnumbered .ch { column-gap: 0; }
.unnumbered .e .pr, .unnumbered .ch span { padding-left: 0.5rem; }
.no { color: var(--accent); font-variant-numeric: tabular-nums lining-nums; font-size: 0.9rem; text-align: right; }
.nm { min-width: 0; overflow-wrap: anywhere; }
.nm b { font-weight: 600; }
sup { font: 0.68rem system-ui, sans-serif; color: var(--muted); margin-left: 0.2rem; }
.tags { margin-left: 0.4rem; }
.tags span.sy { border: 0; padding: 0; margin-right: 0.15rem; color: var(--accent); }
svg.ic { width: 1.1em; height: 1.1em; vertical-align: -0.2em; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.tags .sy svg.ic { width: 1.25rem; height: 1.25rem; }
footer .legend svg.ic { margin-right: 0.15rem; }
.tags span { font: 0.68rem system-ui, sans-serif; letter-spacing: 0.04em; color: var(--accent); border: 1px solid var(--line); border-radius: 999px; padding: 0 0.4rem; margin-right: 0.25rem; white-space: nowrap; }
.d { margin: 0.15rem 0 0; font-size: 0.92rem; font-style: italic; color: var(--muted); hyphens: auto; -webkit-hyphens: auto; text-wrap: pretty; }
.vl { min-width: 0; overflow-wrap: anywhere; font-size: 0.92rem; font-style: italic; color: var(--muted); padding-top: 0.15rem; }
.n { margin: 0.1rem 0 0; font: 0.8rem system-ui, sans-serif; color: var(--accent); }
.pr { font-variant-numeric: tabular-nums lining-nums; text-align: right; white-space: nowrap; }
.e.am { grid-template-columns: var(--no) minmax(0, 1fr) var(--am, 3.4rem) 4.6rem; row-gap: 0.1rem; }
.ml { font-variant-numeric: tabular-nums lining-nums; text-align: right; white-space: nowrap; }
.e.c1, .ch.c1 { grid-template-columns: var(--no) minmax(0, 1fr) 3.4rem; }
.e.c2, .ch.c2 { grid-template-columns: var(--no) minmax(0, 1fr) repeat(2, 3.4rem); }
.e.c3, .ch.c3 { grid-template-columns: var(--no) minmax(0, 1fr) repeat(3, 3.4rem); }
.e.c4, .ch.c4 { grid-template-columns: var(--no) minmax(0, 1fr) repeat(4, 3.2rem); }
.ch { display: grid; column-gap: 0.5rem; padding: 0.4rem 0 0; }
.ch span { font: 0.72rem system-ui, sans-serif; letter-spacing: 0.06em; color: var(--muted); text-align: right; white-space: nowrap; }
.ch.ph { display: block; text-align: right; }
.un { text-transform: none !important; font-variant: normal !important; }
.sv .vl { font-style: italic; }
.e.alt { border-bottom: 0; padding-bottom: 0.15rem; }
.e.ao, .gc + .e { padding-top: 0.15rem; }
.or { font-size: 0.92rem; font-style: italic; color: var(--muted); padding: 0 0 0 calc(var(--no) + 0.5rem); }
.cm { text-align: center; color: var(--muted); padding: 0.15rem 0 0.35rem; letter-spacing: 0; }
.cm.l::before { content: ""; display: block; width: 3.5rem; margin: 0.45rem auto; border-top: 1px solid currentColor; opacity: 0.7; }
.gc { font-variant: small-caps; letter-spacing: 0.05em; color: var(--accent); padding: 0.7rem 0 0 calc(var(--no) + 0.5rem); }
/* A subsection (S44): as a course's title, with more air above; its first dish close below. */
.us { font-variant: small-caps; letter-spacing: 0.05em; color: var(--accent); padding: 1.1rem 0 0 calc(var(--no) + 0.5rem); }
.us h3 { margin: 0; font: inherit; letter-spacing: inherit; color: inherit; }
.unnumbered .us { padding-left: 0; }
.us + .e { padding-top: 0.15rem; }
.unnumbered .or, .unnumbered .gc { padding-left: 0; }
/* A set menu (S32 § 12): the courses' titles one step in, the dishes and „oder“ one further. */
.set { --step: 1.1rem; }
.set .gc { padding-left: calc(var(--no) + 0.5rem); }
.unnumbered .set .gc { padding-left: var(--step); }
.set .e:not(.sv) { padding-left: var(--step); }
.unnumbered .set .e:not(.sv) { padding-left: calc(2 * var(--step)); }
.set .or { padding-left: calc(var(--no) + 0.5rem + var(--step)); }
.unnumbered .set .or { padding-left: calc(2 * var(--step)); }
.wp { margin: 0.1rem 0 0; font-size: 0.92rem; font-variant-numeric: tabular-nums lining-nums; }
.gc .zw { font-variant: normal; letter-spacing: 0; font-style: italic; font-size: 0.85em; color: var(--muted); }
.e.jn { padding-top: 0.15rem; }
/* The recommendations (S37, L2): a box above the menu. */
.emp { border: 1.5px solid var(--accent); border-radius: 14px; padding: 0.1rem 1rem 0.5rem; margin: 1.2rem 0 0.6rem; }
.emp h2.et { margin-top: 0.9rem; }
.emp section h2 { font-size: 1.2rem; margin-top: 1rem; }
/* Sold out (S39): the word stands out, the rest pale, the prices struck through. */
.so .nm b, .so .d, .so .n, .so sup, .so .tags, .so .no, .so .vl, .so .ml { opacity: 0.5; }
.so .pr { opacity: 0.5; text-decoration: line-through; }
section.so .gc, section.so .or, section.so .note { opacity: 0.5; }
.sx { font: 0.68rem system-ui, sans-serif; letter-spacing: 0.04em; text-transform: uppercase; color: var(--bg); background: var(--accent); border-radius: 999px; padding: 0.05rem 0.45rem; margin-left: 0.4rem; white-space: nowrap; vertical-align: 0.1em; }
/* Without any course's title the dishes one step in, as on paper. */
.set.nt .e:not(.sv) { padding-left: 0; }
.unnumbered .set.nt .e:not(.sv) { padding-left: var(--step); }
.set.nt .or { padding-left: calc(var(--no) + 0.5rem); }
.unnumbered .set.nt .or { padding-left: var(--step); }
footer { max-width: var(--page); margin: 0 auto; padding: 1.2rem 16px 2.5rem; border-top: 1px solid var(--line); font: 0.8rem/1.5 system-ui, sans-serif; color: var(--muted); text-align: center; }
footer p { margin: 0 0 0.5rem; }
footer a { color: inherit; }
footer .legend { text-align: left; }
.msg { max-width: 30rem; margin: 18vh auto 0; padding: 0 16px; text-align: center; }
.msg h1 { font-weight: normal; font-size: 1.6rem; margin: 0 0 0.6rem; }
.msg p { color: var(--muted); font-family: system-ui, sans-serif; font-size: 0.95rem; }
body.tabs > input { position: absolute; opacity: 0; pointer-events: none; }
body.tabs .speisen, body.tabs .getraenke.own { padding-bottom: calc(var(--tabbar) + env(safe-area-inset-bottom)); }
#tab-g:checked ~ .speisen, #tab-s:checked ~ .getraenke { display: none; }
.getraenke iframe { position: fixed; top: 0; left: 0; width: 100%; height: calc(100% - var(--tabbar) - env(safe-area-inset-bottom)); border: 0; background: var(--bg); }
.tabbar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 2; display: flex; height: calc(var(--tabbar) + env(safe-area-inset-bottom)); padding: 0 max(0px, calc((100% - var(--page)) / 2)) env(safe-area-inset-bottom); background: var(--nav); border-top: 1px solid var(--line); }
.tabbar label { flex: 1; display: flex; align-items: center; justify-content: center; font: 600 1rem system-ui, sans-serif; color: var(--muted); cursor: pointer; border-top: 3px solid transparent; }
#tab-s:checked ~ .tabbar label[for="tab-s"], #tab-g:checked ~ .tabbar label[for="tab-g"] { color: var(--accent); border-top-color: var(--accent); }
#tab-s:focus-visible ~ .tabbar label[for="tab-s"], #tab-g:focus-visible ~ .tabbar label[for="tab-g"] { outline: 2px solid var(--accent); outline-offset: -4px; }
`;

/** Where each style goes on the page for the phone. */
const WEB_SELECTORS: Record<StyleKey, string> = {
  house: "header .house",
  title: "header .sub",
  lead: "header .lead",
  section: "h2",
  sectionNote: "section > .note",
  number: ".no",
  dish: ".nm b",
  description: ".d",
  variant: ".vl",
  price: ".e .pr, h2 .pr, .e .ml",
  codes: "sup",
  tags: ".tags span",
  itemNote: ".n",
  textTitle: ".x .tt",
  text: ".x .tb",
  textLine: ".x .tl",
  course: ".gc",
  subsection: ".us",
  orLine: ".or, .cm",
  part: "h1.part",
  sizeHead: ".ch span",
  menuNote: "footer .mnote",
  legend: "footer",
};
const shape = (s: TextStyle, k: StyleKey) =>
  `font-family: ${cssFamily(s)}; font-size: ${webSize(k, s.size).toFixed(1)}px; font-weight: ${s.bold ? 700 : 400}; font-style: ${s.italic ? "italic" : "normal"}; text-transform: ${s.caps === "upper" ? "uppercase" : "none"}; font-variant: ${cssVariant(s)}; letter-spacing: ${s.tracking / 100}em;`;

/** Points on paper to pixels on the phone, and the largest a part may get there. */
const PX_PER_PT = 1.62;
const WEB_MAX: Partial<Record<StyleKey, number>> = { house: 40, section: 30, textTitle: 26 };
/** The parts read as text; below the templates they give way less (T13). */
const READ: ReadonlySet<StyleKey> = new Set(["lead", "sectionNote", "number", "dish", "description", "variant", "price", "itemNote", "text", "textLine", "orLine", "menuNote"]);
/** What counts beyond the templates' span (T13). */
const DAMPING = 0.3;
const span = (k: StyleKey) => {
  const sizes = PRESETS.map((p) => p.design.styles[k].size * PX_PER_PT);
  return { lo: Math.min(...sizes) * (READ.has(k) ? 0.9 : 0.7), hi: Math.max(...sizes) };
};

/**
 * The size of a part on the phone (T13, docs/13-typografie.md): within the span the templates use
 * for it as on paper, beyond it only by 30 % – a style made large to fill a page with eight
 * dishes, or small for forty, stays readable on the phone; then within bounds.
 */
export function webSize(k: StyleKey, pt: number): number {
  const { lo, hi } = span(k);
  const px = pt * PX_PER_PT;
  const damped = px > hi ? hi + (px - hi) * DAMPING : px < lo ? lo - (lo - px) * DAMPING : px;
  return Math.max(11, Math.min(WEB_MAX[k] ?? 24, damped));
}

/**
 * The style (S15) for the phone: fonts, weight, slant, capitals, spacing; sizes from the paper's,
 * damped beyond the templates (webSize); the colours only on a light screen (on a dark one the page keeps its own).
 */
function webCss(d: Design): string {
  const faces = designFiles(d).map((file) => {
    const { family, weight, style } = fontFaceOf(file);
    return `@font-face { font-family: "${family}"; font-weight: ${weight}; font-style: ${style}; font-display: swap; src: url("/fonts/${file}") format("woff"); }`;
  });
  const keys = Object.keys(WEB_SELECTORS) as StyleKey[];
  return [
    ...faces,
    ...keys.map((k) => `${WEB_SELECTORS[k]} { ${shape(d.styles[k], k)} }`),
    `header .head { max-width: ${d.headWidth}%; }`,
    d.headAlign === "center" ? "header:not(.side) .head { margin: 0 auto; }" : "",
    d.variantIndent ? `.vl { padding-left: min(${Math.round(d.variantIndent * PX_PER_PT)}px, 25vw); }` : "",
    "footer .legend b { font-weight: 700; }",
    `header:not(.side) { text-align: ${d.headAlign}; }`,
    d.headAlign === "left" ? "header:not(.side) .logo { margin-left: 0; }" : "",
    `@media not (prefers-color-scheme: dark) { ${keys.map((k) => `${WEB_SELECTORS[k]} { color: ${d.styles[k].color}; }`).join(" ")} }`,
  ].join("\n");
}

const page = (title: string, body: string, bodyClass = "", css = "", lang = "de") =>
  `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover"><meta name="robots" content="noindex"><meta name="referrer" content="no-referrer"><meta name="color-scheme" content="light dark"><title>${esc(title)}</title><style>${CSS}${css}</style></head><body${bodyClass ? ` class="${bodyClass}"` : ""}>${body}</body></html>`;

/** The page when there is no menu to show: never published, withdrawn, or an unknown link. */
export function webMessage(title: string, message: string, house = "", drinks: string | null = null): string {
  // A line break in the message stays one („… nicht im Netz.“ / „Bitte fragen Sie das Personal.“).
  const msg = `<div class="msg">${house ? `<header><div class="house">${esc(house)}</div></header>` : ""}<h1>${esc(title)}</h1><p>${message.split("\n").map(esc).join("<br>")}</p></div>`;
  if (drinks) return tabbed(house ? `${house} · ${title}` : title, msg, drinks, "", "getraenke");
  return page(house ? `${house} · ${title}` : title, msg);
}

/** Links on KellerLog's page open outside the frame (the frame may open new windows, nothing else). */
const framed = (html: string) => html.replace(/<a\s+href="(https:[^"]*)"/gi, '<a target="_blank" href="$1"');

/** KellerLog's page in a frame without rights. */
const frameOf = (drinks: string) => `<iframe sandbox="allow-popups allow-popups-to-escape-sandbox" title="Getränkekarte" srcdoc="${esc(framed(drinks))}"></iframe>`;

/**
 * The two tabs at the bottom; `start`: the tab shown first. `drinks` is KellerLog's page (in a frame)
 * or, with `own` (S22), the menu's own drinks as part of this page.
 */
function tabbed(title: string, food: string, drinks: string, bodyClass: string, start: "speisen" | "getraenke" = "speisen", css = "", own: { foodClass: string; drinksClass: string } | null = null, w: { food: string; drinks: string } = { food: "Speisen", drinks: "Getränke" }, lang = "de"): string {
  const pane = own ? `<div class="getraenke own ${own.drinksClass}">${drinks}</div>` : `<div class="getraenke">${frameOf(drinks)}</div>`;
  return page(
    title,
    `<input type="radio" name="tab" id="tab-s" aria-label="${esc(w.food)}"${start === "speisen" ? " checked" : ""}><input type="radio" name="tab" id="tab-g" aria-label="${esc(w.drinks)}"${start === "getraenke" ? " checked" : ""}><div class="speisen${own?.foodClass ? ` ${own.foodClass}` : ""}">${food}</div>${pane}<nav class="tabbar" aria-label="Karte"><label for="tab-s">${esc(w.food)}</label><label for="tab-g">${esc(w.drinks)}</label></nav>`,
    `tabs ${bodyClass}`.trim(),
    css,
    lang,
  );
}

/** The lines below a set menu's courses (S22): its labelled price and further prices, as a dish's. */
function setLines(s: PublishedSection): string {
  const lines = [...(s.priceLabel ? [{ label: s.priceLabel, price: s.price, codes: [] as string[] }] : []), ...(s.variants ?? [])];
  return lines.map((v) => `<li class="e sv"><span></span><span class="vl">${escUnits(v.label)}${v.codes.length ? `<sup>${esc(v.codes.join(","))}</sup>` : ""}</span><span class="pr">${euro(v.price)}</span></li>`).join("");
}

/** Which language a page shows and which it offers (S36): the switch at the top, links without script. */
export interface WebLang {
  current: Lang | "de";
  offered: readonly Lang[];
  /** Where a language's page is; by default `?lang=en`. */
  href?: (lang: Lang | "de") => string;
}

/** The languages a page offers: those the business has, of which the menu has texts. */
export const webLangsOf = (m: Pick<PublishedMenu, "texts">, languages: readonly Lang[]): Lang[] => LANGS.filter((l) => languages.includes(l) && m.texts?.[l]);

/**
 * The language a guest gets (S36): one asked for by the link, else the first of the browser's
 * (`Accept-Language`) the page has, German when none of them. From household-kit (lang).
 */
export { webLangOf } from "@household-kit/lang";

/** Layout D: the published menu as one page for a phone; `drinks`: KellerLog's published card, or null. */
export function menuWebHtml(m: PublishedMenu, settings: WebSettings, drinks: string | null = null, lang: WebLang = { current: "de", offered: [] }, at?: number, dayHints: readonly string[] = []): string {
  // The tab „Getränke“ from one place only (S59): KellerLog's or none leave the menu's own out.
  if (settings.drinksFrom === "kellerlog" || settings.drinksFrom === "none") m = { ...m, sections: m.sections.filter((s) => s.part !== "drinks") };
  if (!kellerlogShown(settings)) drinks = null;
  // In a further language (S36) its texts, frozen with the menu, and the page's own words in it.
  const current = lang.current !== "de" && m.texts?.[lang.current] ? lang.current : "de";
  if (current !== "de") m = menuIn(m, current);
  const w = cardWords(current === "de" ? {} : { lang: { lang: current, both: false } });
  // Zeiten im Netz (S62): with the moment given, a temporary section only in its time, the others named at the top.
  const timed = at !== undefined ? sectionsAt(m, at, current) : null;
  if (timed) m = timed.menu;
  // The day of the weekly plan (S62, Stufe 2) first, then the temporary sections left out.
  const lines = [...dayHints, ...(timed?.hints ?? [])];
  const tline = lines.length ? `<p class="tnote" data-times>${lines.map(escText).join(" · ")}</p>` : "";
  const href = lang.href ?? ((l: Lang | "de") => `?lang=${l}`);
  const others = lang.offered.filter((l) => m.texts?.[l] || l === current);
  const switcher = others.length
    ? `<nav class="langs" aria-label="Sprache · Language">${(["de", ...others] as (Lang | "de")[]).map((l) => (l === current ? `<b aria-current="page">${LANG_SHORT[l]}</b>` : `<a href="${esc(href(l))}" hreflang="${l}" lang="${l}" title="${l === "de" ? "Deutsch" : LANG_NATIVE[l]}">${LANG_SHORT[l]}</a>`)).join(" · ")}</nav>`
    : "";
  // The menu's own drinks (S22) take the tab „Getränke“; KellerLog's page then stays out.
  const foodSections = m.sections.filter((s) => s.part !== "drinks");
  const drinkSections = m.sections.filter((s) => s.part === "drinks");
  const numberedIn = (list: PublishedSection[]) => list.some((s) => s.items.some((i) => i.number !== null));
  const navOf = (list: PublishedSection[], prefix: string) =>
    list.filter((s) => s.title).length > 1 ? `<nav class="jump" aria-label="Abschnitte der Speisekarte">${list.map((s, i) => (s.title ? `<a href="#${prefix}${i}">${esc(s.title)}</a>` : "")).join("")}</nav>` : "";
  // The symbols of the recommendations with the menu's (S37), for their items and the legend.
  const icons = m.lead?.tagIcons ? { ...m.lead.tagIcons, ...m.tagIcons } : m.tagIcons;
  // The mark between courses (S57) when the style holds for the net too.
  const mark = m.design?.web ? (designOf(m.design).courseMark ?? null) : null;
  const bodyOf = (list: PublishedSection[], prefix: string) =>
    list
      .map((s, i) => {
        const set = s.kind === "set";
        const labelled = set && Boolean(s.priceLabel);
        // A set menu sold out as a whole (S39): the word after its title, the rest pale.
        const sold = s.soldOut ? `<span class="sx">${esc(w.soldOut)}</span>` : "";
        const head = s.title || set ? `<h2><span>${escText(s.title || w.setMenu)}${sold}</span>${set && s.price !== null && !labelled ? `<span class="pr">${euro(s.price)}</span>` : ""}</h2>` : "";
        const items = set ? setItems(s.items, icons, current === "de" ? m.orText : (m.orText ?? w.or), current === "de" ? m.choiceText : (m.choiceText ?? w.choice), w.soldOut, mark) : s.items.map((it) => `${it.sub ? `<li class="us"><h3>${escText(it.sub)}</h3></li>` : ""}${item(it, set, icons, false, w.soldOut)}`).join("");
        // „30 cm“ over the prices of a section (S46), right above them.
        const priceHead = !set && s.priceHead ? `<li class="ch ph"><span>${escUnits(s.priceHead)}</span></li>` : "";
        // The column of amounts (S51) as wide as the section's longest, in letters.
        const longest = set ? 0 : Math.max(0, ...s.items.filter((x) => x.amounts).flatMap((x) => priceLines(x, false).map((v) => v.label.length)));
        const am = longest ? ` style="--am: ${Math.min(longest, 16) * 0.55 + 0.6}em"` : "";
        return `<section id="${prefix}${i}"${s.soldOut ? ' class="so"' : ""}>${head}${s.note ? `<p class="note">${escText(s.note)}</p>` : ""}<ul class="items${set ? ` set${s.items.some((x) => x.course) ? "" : " nt"}` : ""}"${am}>${priceHead}${items}${set ? setLines(s) : ""}</ul></section>`;
      })
      .join("");
  const onlyDrinks = foodSections.length === 0 && drinkSections.length > 0;
  const mainSections = onlyDrinks ? drinkSections : foodSections;
  const numbered = numberedIn(mainSections);
  const nav = navOf(mainSections, "a");
  const body = bodyOf(mainSections, "a");
  // The recommendations (S37, L2) in a box above the menu, under the one head of the page.
  const lead = m.lead && m.lead.sections.some((s) => s.items.length)
    ? `<section class="emp" id="empfehlungen"><h2 class="et"><span>${esc(m.lead.title || w.recommendations)}</span></h2>${m.lead.subtitle ? `<p class="note">${esc(m.lead.subtitle)}</p>` : ""}${bodyOf(m.lead.sections.filter((s) => s.items.length), "e")}${m.lead.note ? `<p class="note">${esc(m.lead.note)}</p>` : ""}</section>`
    : "";
  const { allergens, additives } = legendLines(legendWithLead(m.legend, m.lead));
  const date = new Date(m.publishedAt).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" });
  const footer = [
    m.note ? `<p class="mnote">${escText(m.note)}</p>` : "",
    allergens ? `<p class="legend"><b>${esc(w.allergens)}:</b> ${esc(allergens)}</p>` : "",
    additives ? `<p class="legend"><b>${esc(w.additives)}:</b> ${esc(additives)}</p>` : "",
    icons && Object.keys(icons).length ? `<p class="legend"><b>${esc(w.symbols)}:</b> ${Object.entries(icons).map(([t, ic]) => `${iconSvg(ic, t)} ${esc(t)}`).join(" · ")}</p>` : "",
    showsPrices(m) ? `<p>${esc(settings.footer || w.footer)} · ${esc(w.asOf)} ${esc(date)}</p>` : "",
    settings.address || settings.imprint || settings.privacy
      ? `<p>${esc(m.house)}${settings.address ? ` · ${esc(settings.address)}` : ""}${settings.imprint ? ` · <a href="${esc(settings.imprint)}" rel="noopener noreferrer">${esc(w.imprint)}</a>` : ""}${settings.privacy ? ` · <a href="${esc(settings.privacy)}" rel="noopener noreferrer">${esc(w.privacy)}</a>` : ""}</p>`
      : "",
    linksFoot(settings),
  ].join("");
  const content = tline + lead + (mainSections.length ? body : lead ? "" : `<div class="msg"><p>${esc(w.empty)}</p></div>`);
  const side = m.logo && m.logoPlace !== "center" ? m.logoPlace : null;
  const size = m.logo ? (side ? fitLogo(m.logo, 130, 110) : fitLogo(m.logo, 240, 110)) : null;
  const logo = m.logo && size ? `<img class="logo" src="data:image/png;base64,${m.logo.data}" alt="${esc(m.house)}" width="${Math.round(size.width)}" height="${Math.round(size.height)}">` : "";
  const house = showsHouse(m.logo, m.logoOnly) ? `<div class="house">${esc(m.house)}</div>` : "";
  const text = `${house}<div class="sub">${esc(m.title || w.card)}</div>${m.subtitle ? `<p class="lead">${escText(m.subtitle)}</p>` : ""}`;
  const head = switcher + (side ? `<header class="side ${side}">${logo}<div class="head">${text}</div></header>` : `<header>${logo}<div class="head">${text}</div></header>`);
  const food = `${head}${nav}<main>${content}</main><footer>${footer}</footer>`;
  const title = `${m.title || w.card} · ${m.house}`;
  const cls = numbered ? "" : "unnumbered";
  // A drink in a set menu (S53) in its look: its own, else the drinks', when either differs from the food's.
  const setDrinks = m.design?.web && (m.design.drinkStyles || m.design.setDrinkStyles) && m.sections.some((s) => s.items.some((i) => i.drink));
  const css = m.design?.web ? webCss(m.design) + (setDrinks ? setDrinksWebCss(m.design) : "") : "";
  if (drinkSections.length && !onlyDrinks) {
    const ownDrinks = `${head}${navOf(drinkSections, "g")}<main>${bodyOf(drinkSections, "g")}</main><footer>${footer}</footer>`;
    return tabbed(title, food, ownDrinks, "", "speisen", css + (m.design?.web && m.design.drinkStyles ? drinksWebCss(m.design) : ""), { foodClass: cls, drinksClass: numberedIn(drinkSections) ? "" : "unnumbered" }, w, current);
  }
  const drinksCss = onlyDrinks && m.design?.web && m.design.drinkStyles ? drinksWebCss(m.design, "body") : "";
  return drinks && !onlyDrinks ? tabbed(title, food, drinks, cls, "speisen", css, null, w, current) : page(title, food, cls, css + drinksCss, current);
}

/** The drinks' own look (S22) on the page: the parts of a dish within `scope`. */
function drinksWebCss(d: Design, scope = ".getraenke.own"): string {
  const within = (k: StyleKey) => WEB_SELECTORS[k].split(",").map((x) => `${scope} ${x.trim()}`).join(", ");
  const own = d.drinkStyles!;
  return [
    ...DRINK_STYLE_KEYS.map((k) => `${within(k)} { ${shape(own[k], k)} }`),
    `@media not (prefers-color-scheme: dark) { ${DRINK_STYLE_KEYS.map((k) => `${within(k)} { color: ${own[k].color}; }`).join(" ")} }`,
  ].join("\n");
}

/** Where the parts of a drink in a set menu (S53, `.wn`) stand on the page. */
const SET_DRINK_SELECTORS: Record<SetDrinkStyleKey, string> = { dish: ".wn .nm b", description: ".wn .d", variant: ".wn .wp .vl", price: ".wn .wp .pr", codes: ".wn sup", tags: ".wn .tags span", itemNote: ".wn .n" };

/** The look of drinks in a set menu (S53) on the page: theirs, else the drinks', coupled as on paper. */
function setDrinksWebCss(d: Design): string {
  const look = setDrinkDesign(d).styles;
  return [
    ...SET_DRINK_STYLE_KEYS.map((k) => `${SET_DRINK_SELECTORS[k]} { ${shape(look[k], k)} }`),
    `@media not (prefers-color-scheme: dark) { ${SET_DRINK_STYLE_KEYS.map((k) => `${SET_DRINK_SELECTORS[k]} { color: ${look[k].color}; }`).join(" ")} }`,
  ].join("\n");
}

/**
 * Whether a page of KellerLog's card on the net shows a card: not „Noch keine Karte“, not „Zurzeit
 * nicht verfügbar“, not „Karte nicht gefunden“ (those pages are a message, `<div class="msg">`, with
 * no list of drinks; packages/core/src/card-web.ts in KellerLog).
 */
export function isDrinksCard(html: string): boolean {
  return /<main>/.test(html) && /<header><div class="house">/.test(html) && !/<body[^>]*><div class="msg">/.test(html);
}
